import React, { useState } from "react";
import {
    Shield, ShieldCheck, ShieldOff, Edit3, LogOut, KeyRound, Mail,
    Unlock, Ban, RotateCcw, Info, History, Monitor,
} from "lucide-react";
import { roleLabel } from "./roleLabels";

const STATUS_CHIP = {
    active:    { label: "Active",    cls: "bg-[#E6F1DE] text-[#2E7E22]" },
    suspended: { label: "Suspended", cls: "bg-[#FEF3C7] text-[#B45309]" },
    disabled:  { label: "Disabled",  cls: "bg-[#FEE2E2] text-[#991B1B]" },
    deleted:   { label: "Deleted",   cls: "bg-[#F5F5F7] text-[#6B7280] line-through" },
    invited:   { label: "Invited",   cls: "bg-[#E1E6F0] text-[#131B2F]" },
};

function fmt(iso) {
    if (!iso) return null;
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function UserDetailPanel({
    user, tab, setTab, onEdit, onAction, renderSessions, renderAudit,
}) {
    const chip = STATUS_CHIP[user.account_status] || STATUS_CHIP.active;
    const lockedNow =
        user.locked_until && new Date(user.locked_until).getTime() > Date.now();

    return (
        <div className="flex flex-col h-full">
            {/* Sub-header — identity + primary actions */}
            <div className="px-4 md:px-6 py-5 bg-white border-b border-[#E5E7EB]">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h2 className="text-lg font-semibold text-[#131B2F] truncate">
                                {user.name || user.email}
                            </h2>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${chip.cls}`}>
                                {chip.label}
                            </span>
                            {user.is_security_admin && (
                                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] text-[#131B2F] bg-[#E1E6F0] px-1.5 py-0.5 rounded">
                                    <Shield size={10} /> Security admin
                                </span>
                            )}
                            {lockedNow && (
                                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] text-[#B45309] bg-[#FEF3C7] px-1.5 py-0.5 rounded">
                                    Locked
                                </span>
                            )}
                        </div>
                        <div className="text-xs text-[#4B5565] mt-0.5">
                            {user.email} · {roleLabel(user.role)}
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <DetailBtn
                            icon={Edit3}
                            label="Edit details"
                            onClick={onEdit}
                            testid="admin-sec-edit"
                        />
                        {user.is_security_admin ? (
                            <DetailBtn
                                icon={ShieldOff}
                                label="Revoke sec admin"
                                onClick={() => onAction({
                                    kind: "revokeSecAdmin", id: user.id, tone: "danger",
                                    label: `Revoke the Security Administrator privilege from ${user.name || user.email}? They will keep their standard access but lose this console.`,
                                })}
                                testid="admin-sec-revoke-priv"
                            />
                        ) : (
                            <DetailBtn
                                icon={ShieldCheck}
                                label="Grant sec admin"
                                onClick={() => onAction({
                                    kind: "grantSecAdmin", id: user.id,
                                    label: `Grant the Security Administrator privilege to ${user.name || user.email}? They will gain access to the full Security Administration console.`,
                                })}
                                testid="admin-sec-grant-priv"
                            />
                        )}
                    </div>
                </div>

                {/* Quick action row */}
                <div className="mt-4 flex flex-wrap gap-2">
                    {(lockedNow || user.failed_login_count > 0) && (
                        <DetailBtn
                            icon={Unlock}
                            label="Unlock"
                            tone="green"
                            onClick={() => onAction({
                                kind: "unlock", id: user.id,
                                label: `Clear the failed-login counter and lock timer for ${user.name || user.email}?`,
                            })}
                        />
                    )}
                    {user.account_status === "active" && (
                        <DetailBtn
                            icon={Ban}
                            label="Suspend"
                            tone="amber"
                            onClick={() => onAction({
                                kind: "suspend", id: user.id, tone: "danger",
                                label: `Suspend ${user.name || user.email}? All active sessions will be revoked and the user will be signed out immediately.`,
                            })}
                        />
                    )}
                    {user.account_status === "suspended" && (
                        <DetailBtn
                            icon={RotateCcw}
                            label="Reactivate"
                            tone="green"
                            onClick={() => onAction({
                                kind: "reactivate", id: user.id,
                                label: `Reactivate ${user.name || user.email}? They will be able to sign in again immediately.`,
                            })}
                        />
                    )}
                    {user.account_status !== "disabled" && (
                        <DetailBtn
                            icon={Ban}
                            label="Disable"
                            tone="red"
                            testid="admin-sec-disable"
                            onClick={() => onAction({
                                kind: "disable", id: user.id, tone: "danger",
                                label: `Disable ${user.name || user.email}? All sessions will be revoked. Only a Security Administrator can re-enable this account.`,
                            })}
                        />
                    )}
                    {user.account_status === "disabled" && (
                        <DetailBtn
                            icon={ShieldCheck}
                            label="Enable account"
                            tone="green"
                            testid="admin-sec-enable"
                            onClick={() => onAction({
                                kind: "enable", id: user.id,
                                label: `Enable ${user.name || user.email}? They will be able to sign in again with their existing password. No projects, permissions or session tokens will be changed.`,
                            })}
                        />
                    )}
                    <DetailBtn
                        icon={LogOut}
                        label="Revoke all sessions"
                        onClick={() => onAction({
                            kind: "revoke", id: user.id, tone: "danger",
                            label: `Revoke every active session for ${user.name || user.email}? They will be signed out on all devices immediately.`,
                        })}
                    />
                    <DetailBtn
                        icon={KeyRound}
                        label="Force password change"
                        onClick={() => onAction({
                            kind: "force", id: user.id,
                            label: `Force ${user.name || user.email} to change their password on next login?`,
                        })}
                    />
                    <DetailBtn
                        icon={KeyRound}
                        label="Reset password"
                        tone="amber"
                        testid="admin-sec-reset-pw"
                        onClick={() => onAction({
                            kind: "resetPassword", id: user.id, tone: "danger",
                            label: `Reset ${user.name || user.email}'s password? A strong temporary password will be generated and shown to you once. All their active sessions will be revoked and they will be forced to set a new password on next sign-in. Existing projects and data are not touched.`,
                        })}
                    />
                    <DetailBtn
                        icon={KeyRound}
                        label="Reset to demo password"
                        tone="amber"
                        testid="admin-sec-reset-demo-pw"
                        onClick={() => onAction({
                            kind: "resetDemoPw", id: user.id, tone: "danger",
                            label: `Reset ${user.name || user.email}'s password to the standard demo password? All their active sessions will be revoked, and they'll be forced to pick a new password on next login. Only available in demo/test pods.`,
                        })}
                    />
                    <DetailBtn
                        icon={Mail}
                        label="Send reset email"
                        onClick={() => onAction({
                            kind: "resetPw", id: user.id,
                            label: `Send a password reset email to ${user.email}?`,
                        })}
                    />
                </div>

                {/* Tabs */}
                <div className="mt-5 flex items-center gap-1 border-b border-[#E5E7EB] -mb-5">
                    <TabBtn active={tab === "overview"} onClick={() => setTab("overview")} icon={Info} testid="admin-sec-tab-overview">
                        Overview
                    </TabBtn>
                    <TabBtn active={tab === "sessions"} onClick={() => setTab("sessions")} icon={Monitor} testid="admin-sec-tab-sessions">
                        Active sessions
                    </TabBtn>
                    <TabBtn active={tab === "audit"} onClick={() => setTab("audit")} icon={History} testid="admin-sec-tab-audit">
                        Security audit
                    </TabBtn>
                </div>
            </div>

            {/* Tab body — scrolls inside the master/detail pane */}
            <div className="flex-1 min-h-0 overflow-y-auto p-4 md:p-6 space-y-4">
                {tab === "overview" && <OverviewGrid user={user} />}
                {tab === "sessions" && renderSessions()}
                {tab === "audit"    && renderAudit()}
            </div>
        </div>
    );
}

function OverviewGrid({ user }) {
    // FIELD-001C · Account role governs app access. Resource type
    // (Electrician, Scaffolder, …) is a separate attribute managed
    // in Field Team Management and only meaningful for a Field User.
    // Missing values render "Not specified" — we never guess a trade.
    const isFieldUser = user.role === "installer";
    const resourceRow = isFieldUser
        ? [["Resource type", user.resource_type || user.engineer_resource_type || "Not specified"]]
        : [];
    const rows = [
        ["Account role",               roleLabel(user.role) || "—"],
        ...resourceRow,
        ["Mobile",                     user.mobile || "—"],
        ["Security Administrator",     user.is_security_admin ? "Yes" : "No"],
        ["Status",                     STATUS_CHIP[user.account_status]?.label || user.account_status],
        ["Last successful login",      fmt(user.last_successful_login_at) || "—"],
        ["Last failed login",          fmt(user.last_failed_login_at) || "—"],
        ["Failed login count",         String(user.failed_login_count || 0)],
        ["Password last changed",      fmt(user.password_changed_at) || "—"],
        ["Password expires",           fmt(user.password_expires_at) || "—"],
        ["MFA",                        user.mfa_enabled ? "Enabled" : "Not enabled"],
        ["Email verified",             fmt(user.email_verified_at) || "—"],
        ["Force password change",      user.force_password_change ? "Yes" : "No"],
        ["Created",                    fmt(user.created_at) || "—"],
        ["Test data",                  user.is_test_data ? "Yes" : "No"],
    ];
    return (
        <div className="bg-white rounded-xl border border-[#E5E7EB]" data-testid="admin-sec-overview">
            <div className="px-4 md:px-5 py-3 border-b border-[#E5E7EB]">
                <div className="text-sm font-semibold text-[#131B2F]">Account overview</div>
            </div>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 p-4 md:p-5 text-xs">
                {rows.map(([k, v]) => (
                    <div key={k} className="flex items-start justify-between gap-3 py-1.5 border-b border-[#F0F1F5] last:border-b-0 md:border-b-0 md:py-1">
                        <dt className="text-[#4B5565]">{k}</dt>
                        <dd className="text-[#131B2F] font-medium text-right break-words">{v || "—"}</dd>
                    </div>
                ))}
            </dl>
        </div>
    );
}

function DetailBtn({ icon: Icon, label, onClick, tone = "neutral", testid }) {
    const toneCls = {
        neutral: "border-[#E5E7EB] text-[#131B2F] hover:bg-[#F5F5F7]",
        green:   "border-[#3E9E2E]/30 text-[#2E7E22] hover:bg-[#3E9E2E]/8",
        amber:   "border-[#F59E0B]/30 text-[#B45309] hover:bg-[#F59E0B]/8",
        red:     "border-[#DA3535]/30 text-[#991B1B] hover:bg-[#DA3535]/8",
    }[tone];
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md border bg-white text-xs font-medium ${toneCls}`}
        >
            <Icon size={13} strokeWidth={1.75} /> {label}
        </button>
    );
}

function TabBtn({ active, onClick, icon: Icon, children, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium -mb-px border-b-2 transition-colors ${
                active
                    ? "text-[#131B2F] border-[#131B2F]"
                    : "text-[#4B5565] border-transparent hover:text-[#131B2F]"
            }`}
        >
            <Icon size={13} strokeWidth={1.75} /> {children}
        </button>
    );
}
